import React, { useState, useEffect, useRef } from 'react';
import { Box,Container,TextField,InputAdornment,IconButton,Snackbar,Alert,Drawer,List,ListItem,ListItemText,Tooltip,Typography,Paper } from '@mui/material';
import ArrowCircleUpTwoToneIcon from '@mui/icons-material/ArrowCircleUpTwoTone';
import { getChatData, saveChat, getUserChats } from '../../actions/supportAssistant';
import { useDispatch } from 'react-redux';
import Markdown from 'markdown-to-jsx';
import { Cursor } from '../../icons/cursor';
import { filterProfanity, containsProfanity } from '../../utils/profanityFilter';
import { Filter } from 'bad-words';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SaveIcon from '@mui/icons-material/Save';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatIcon from '@mui/icons-material/Chat';
import { deleteChat, fetchSavedChats } from '../../actions/supportAssistant';

const SupportAssistant = () => { 
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [completedTyping, setCompletedTyping] = useState(false);
  const [displayResponse, setDisplayResponse] = useState("");
  const messagesEndRef = useRef(null);  
  const filter = new Filter();
  const [openAlert, setOpenAlert] = useState(false);
  const { currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [savedChats, setSavedChats] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('Please keep the conversation respectful.');
  const [alertSeverity, setAlertSeverity] = useState('warning');
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(scrollToBottom, [messages]);

  useEffect(scrollToBottom, [displayResponse]);

  useEffect(() => {
    if(messages === undefined || messages.length === 0){
      return;
    }
    setCompletedTyping(false);
  
    let i = 0;
     
    const stringResponse = messages[messages.length - 1].text
  
    const intervalId = setInterval(() => {
      setDisplayResponse(stringResponse.slice(0, i));
  
      i++;
  
      if (i > stringResponse.length) {
        clearInterval(intervalId);
        setCompletedTyping(true);
      }
    }, 20);
  
  return () => clearInterval(intervalId);    
  }, [messages]);

  const handleKeyPress = (e) =>{
    if(e.charCode === 13){
      handleSendMessage();
    }
  };

  const checkProfanity = (text) => {
    try {
      return filter.isProfane(text) || containsProfanity(text);
    } catch (error) {
      return containsProfanity(text);
    }
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const handleSendMessage = async() => {
    if (!searchQuery.trim()) 
        return;
      
    if (checkProfanity(searchQuery)) {
        setOpenAlert(true);
        return;
    }
    
    const filteredMessage = filterProfanity(searchQuery.trim());
    
    const userMessage = { text: filteredMessage, isAI: false };
    const body = {
        chatHistory: [...messages, userMessage], 
        searchQuery: filteredMessage,
    }    
    
    setMessages([...messages, userMessage]);         
    setSearchQuery('');   
    const response = await dispatch(getChatData(body)); 
    const aiMessage = { text: response.message, isAI: true };
    setMessages(currentMessages => [...currentMessages, aiMessage]); 
  };

  const handleSaveChat = async () => {
    if (!isAuthenticated || messages.length === 0) return;

    try {
        const token = localStorage.getItem('token');
        if (!token) {
            setAlertMessage('Please sign in to save chats');
            setAlertSeverity('warning');
            setOpenAlert(true);
            return;
        }

        const config = {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };

        const formattedMessages = messages.map(msg => ({
            text: msg.text,
            isAI: msg.isAI
        }));

        const payload = {
            messages: formattedMessages,
            chatId: currentChatId
        };
        
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/chats`,
            payload,
            config
        );
        
        if (response.data) {
            setCurrentChatId(response.data._id);
            await fetchSavedChats();
            setAlertMessage(currentChatId ? 'Chat updated successfully!' : 'Chat saved successfully!');
            setAlertSeverity('primary');
            setOpenAlert(true);
        }
    } catch (error) {
        if (error.response?.status === 401) {
            setAlertMessage('Session expired. Please sign in again.');
            setAlertSeverity('error');
            localStorage.removeItem('token');
            navigate('/auth');
        } else {
            setAlertMessage(
                currentChatId 
                    ? 'Failed to update chat. Please try again.' 
                    : 'Failed to save chat. Please try again.'
            );
            setAlertSeverity('error');
        }
        setOpenAlert(true);
    }
  };

  const fetchSavedChats = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            setAlertMessage('Please sign in to view saved chats');
            setAlertSeverity('warning');
            setOpenAlert(true);
            return;
        }

        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/chats`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
        
        if (response.data) {
            setSavedChats(response.data);
        }
    } catch (error) {
        console.error('Fetch chats error:', error);
        if (error.response?.status === 401) {
            setAlertMessage('Session expired. Please sign in again.');
            setAlertSeverity('error');
            localStorage.removeItem('token');
            navigate('/auth');
        }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && isAuthenticated) {
        fetchSavedChats();
    } else {
        setSavedChats([]);
    }
  }, [isAuthenticated]);

  const handleLoadChat = (chat) => {
    setMessages(chat.messages);
    setCurrentChatId(chat._id);
    setSidebarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
        setIsLoading(true);
        const response = await dispatch(getChatData(searchQuery));
        
        if (response) {
            // User message
            const userMessage = { 
                text: searchQuery, 
                isAI: false 
            };
            
            // AI message
            const aiMessage = { 
                text: response.message, 
                isAI: true
            };

            const newMessages = [...messages, userMessage, aiMessage];
            setMessages(newMessages);
            setSearchQuery('');

            if (currentChatId) {
                await dispatch(saveChat(newMessages, currentChatId));
            }
        }
    } catch (error) {
        console.error('Error:', error);
        setAlertMessage(error.message || 'Failed to get response');
        setAlertSeverity('error');
        setOpenAlert(true);
    } finally {
        setIsLoading(false);
    }
  };

  const renderSaveButton = () => (
    <Tooltip title="Save Chat">
      <span>
        <IconButton 
          onClick={handleSaveChat}
          disabled={!isAuthenticated || messages.length === 0}
          sx={{ 
            ml: 1,
            color: 'primary.main',
            '&:hover': {
              color: 'primary.dark'
            }
          }}
        >
          <SaveIcon />
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderSavedChatsButton = () => (
    <Tooltip title="Saved Chats">
      <span>
        <IconButton 
          onClick={() => setSidebarOpen(true)}
          disabled={!isAuthenticated}
          sx={{ 
            ml: 1,
            color: 'primary.main',
            '&:hover': {
              color: 'primary.dark'
            }
          }}
        >
          <BookmarksIcon />
        </IconButton>
      </span>
    </Tooltip>
  );

  const handleDeleteChat = async (chatId, event) => {
    event.stopPropagation();
    
    try {
        await dispatch(deleteChat(chatId));
        
        if (chatId === currentChatId) {
            clearChat();
        }

        await fetchSavedChats();
        setSidebarOpen(false);
        
        setAlertMessage('Chat deleted successfully');
        setAlertSeverity('primary');
        setOpenAlert(true);
    } catch (error) {
        console.error('Delete error:', error);
        if (error.message.includes('token')) {
            setAlertMessage('Please sign in again to delete chats');
            setAlertSeverity('secondary');
            localStorage.removeItem('token');
            navigate('/auth');
        } else {
            setAlertMessage('Failed to delete chat');
            setAlertSeverity('error');
        }
        setOpenAlert(true);
    }
  };

  const renderSidebar = () => (
    <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
            sx: {
                width: 280,
                background: 'white',
                p: 2
            }
        }}
    >
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2,
            borderBottom: '2px solid rgba(25, 118, 210, 0.2)',
            pb: 1
        }}>
            <Typography 
                variant="h6" 
                sx={{ 
                    color: 'primary.main',
                    fontWeight: 600
                }}
            >
                Saved Chats
            </Typography>
            <IconButton 
                onClick={() => setSidebarOpen(false)}
                sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                        color: 'primary.dark'
                    }
                }}
            >
                <CloseIcon />
            </IconButton>
        </Box>
        <List>
            {savedChats.map((chat) => (
                <ListItem 
                    key={chat._id}
                    button
                    onClick={() => handleLoadChat(chat)}
                    sx={{
                        mb: 1,
                        borderRadius: '10px',
                        '&:hover': {
                            background: 'rgba(25, 118, 210, 0.08)',
                        }
                    }}
                    secondaryAction={
                        <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={(e) => handleDeleteChat(chat._id, e)}
                            sx={{ 
                                color: 'error.light',
                                '&:hover': {
                                    color: 'error.main'
                                }
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    }
                >
                    <ListItemText 
                        primary={
                            <Typography 
                                sx={{ 
                                    color: 'primary.main',
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    pr: 4 // Add padding to prevent text overlap with delete button
                                }}
                            >
                                {chat.messages[0]?.text || 'Empty chat'}
                            </Typography>
                        }
                        secondary={
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'primary.light',
                                    fontSize: '0.85rem'
                                }}
                            >
                                {new Date(chat.createdAt).toLocaleDateString()}
                            </Typography>
                        }
                    />
                </ListItem>
            ))}
            {savedChats.length === 0 && (
                <Box 
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '200px',
                        color: 'primary.main',
                        opacity: 0.7
                    }}
                >
                    <ChatIcon sx={{ 
                        fontSize: 48, 
                        mb: 2,
                        color: 'primary.main'
                    }} />
                    <Typography>No saved chats yet</Typography>
                </Box>
            )}
        </List>
    </Drawer>
  );

  const renderMessage = (message, index) => {
    return (
        <div key={index}>
            {!message.isAI && (
                <div className="chat chat-end">
                    <span className="user-message" style={{
                        background: 'transparent',
                        color: '#764ba2',
                        padding: '10px 15px',
                        borderRadius: '15px',
                        margin: '5px',
                        display: 'inline-block',
                        maxWidth: '80%',
                        float: 'right',
                        clear: 'both',
                        fontWeight: '500'
                    }}>
                        {message.text}
                    </span>
                </div>
            )}
            {message.isAI && index === messages.length - 1 && (
                <div className="chat chat-start">
                    <span className="chat-bubble whitespace-pre-line" style={{
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        color: 'white',
                        padding: '10px 15px',
                        borderRadius: '15px',
                        margin: '5px',
                        display: 'inline-block',
                        maxWidth: '80%',
                        float: 'left',
                        clear: 'both',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        {displayResponse}
                        {!completedTyping && <Cursor />}
                    </span>
                </div>
            )}
            {message.isAI && index !== messages.length - 1 && (
                <div className="chat chat-start">
                    <span className="chat-bubble whitespace-pre-line" style={{
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        color: 'white',
                        padding: '10px 15px',
                        borderRadius: '15px',
                        margin: '5px',
                        display: 'inline-block',
                        maxWidth: '80%',
                        float: 'left',
                        clear: 'both',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        {message.text}
                    </span>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentChatId(null);
  };

  useEffect(() => {
    if (messages.length === 0) {
        setCurrentChatId(null);
    }
  }, [messages]);



  return (
    <>
        {renderSidebar()}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            maxHeight: '100vh',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              width: '100%',
              pb: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-start',
                alignItems: 'center',
                mb: 2,
                gap: 1
            }}>
                {renderSavedChatsButton()}
                {renderSaveButton()}
            </Box>
            <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                marginBottom: 2
            }} className="grow overflow-y-auto">
                {messages.length === 0 && (
                    <Box
                        sx={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: '#4a5568',
                            maxWidth: '600px',
                            margin: '2rem auto',
                        }}
                    >
                        <h1 style={{ 
                            color: '#4A3428',
                            marginBottom: '1rem',
                            fontSize: '1.8rem',
                            fontWeight: '600',
                            fontFamily: '"Literata", Georgia, serif'
                        }}>
                            Welcome to Your Safe Space
                        </h1>
                        <p style={{
                            fontSize: '1.1rem',
                            lineHeight: '1.6',
                            marginBottom: '1rem'
                        }}>
                            Here, every rejection becomes a stepping stone towards growth. Whether you&apos;re dealing with college applications, job applications, relationships, or personal goals, you&apos;re not alone in this journey.
                        </p>
                        <p style={{
                            fontSize: '1.1rem',
                            lineHeight: '1.6',
                            marginBottom: '1rem'
                        }}>
                            Feel free to share your experiences, feelings, or concerns. Together, we&apos;ll transform these moments into opportunities for learning and resilience.
                        </p>
                        <p style={{
                            fontSize: '1.1rem',
                            fontStyle: 'italic',
                            color: '#8B6F5C'
                        }}>
                            Start by sharing your feelings...
                        </p>
                    </Box>
                )}
                <div className="grow overflow-y-auto">
                    {messages.map((message, index) => renderMessage(message, index))}
                </div>
            </Box>        
          </Box>
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              width: '100%',
              backgroundColor: 'background.paper',
              p: { xs: 1, sm: 2 },
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <TextField
              id="search-bar"
              fullWidth
              value={searchQuery}
              onChange={(e) => {
                  setSearchQuery(e.target.value);
              }}
              label="How do you feel.."
              variant="outlined"
              placeholder="Search..."
              size="medium"
              color='primary'
              onKeyPress={handleKeyPress}
              sx={{
                  '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      padding: '4px 8px',
                      '& fieldset': {
                          borderWidth: '2px',
                          borderColor: 'rgba(102, 126, 234, 0.3)',
                      },
                      '&:hover fieldset': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                          borderWidth: '2px',
                      },
                  },
                  '& .MuiInputLabel-root': {
                      fontSize: '1.1rem',
                      '&.Mui-focused': {
                          color: '#667eea',
                      }
                  },
                  '& .MuiInputBase-input': {
                      fontSize: '1.1rem',
                      padding: '12px 14px',
                  }
              }}
              InputProps={{
                  endAdornment: (
                      <InputAdornment position="end">
                          <IconButton onClick={handleSendMessage}>              
                              <ArrowCircleUpTwoToneIcon 
                                  fontSize="large"
                                  sx={{ color: '#667eea' }}
                              />
                          </IconButton>
                      </InputAdornment>
                  )
              }}
            />
          </Box>
        </Box>
        <Snackbar 
            open={openAlert} 
            autoHideDuration={6000} 
            onClose={() => setOpenAlert(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert 
                onClose={() => setOpenAlert(false)} 
                severity={alertSeverity}
                sx={{
                    '&.MuiAlert-standardPrimary': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        '& .MuiAlert-icon': {
                            color: 'primary.contrastText'
                        }
                    },
                    '&.MuiAlert-standardSecondary': {
                        backgroundColor: 'secondary.light',
                        color: 'secondary.contrastText',
                        '& .MuiAlert-icon': {
                            color: 'secondary.contrastText'
                        }
                    },
                    '&.MuiAlert-standardError': {
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        '& .MuiAlert-icon': {
                            color: 'error.contrastText'
                        }
                    }
                }}
            >
                {alertMessage}
            </Alert>
        </Snackbar>
    </>
  );
}

export default SupportAssistant;